import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import React, {useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {color} from "@khanacademy/wonder-blocks-tokens";

import starIconFilled from "@phosphor-icons/core/fill/star-fill.svg";
import starIcon from "@phosphor-icons/core/regular/star.svg";

interface FavoriteButtonProps {
    setIsFavorited: (isFavorited: boolean) => void;
    isFavorited: boolean;
    configName: string;
    updateFullToolList?: (configName: string, isFavorited: boolean) => void;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
    setIsFavorited,
    isFavorited,
    configName,
    updateFullToolList,
}) => {
    const [showAnimation, setShowAnimation] = useState(false);
    const timeout = useTimeout(() => setShowAnimation(false), 1300);

    const handleStarIconClick = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (updateFullToolList) {
            updateFullToolList(configName, !isFavorited);
        }
        setIsFavorited(!isFavorited);
        setShowAnimation(true);
        timeout.set();
    };

    return (
        <View>
            <IconButton
                testId="favorite-button"
                icon={isFavorited ? starIconFilled : starIcon}
                aria-label={
                    isFavorited
                        ? t`Click to unfavorite this tool`
                        : t`Click to favorite this tool`
                }
                onClick={handleStarIconClick}
                kind="tertiary"
                actionType="neutral"
                size="medium"
                style={isFavorited && styles.hoverFavoriteButton}
            />
            {showAnimation && isFavorited && (
                <svg
                    className={css(styles.starAnimation)}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                >
                    <path
                        className={css(styles.star1)}
                        d="M12 2.5l1.09 3.36h3.54l-2.87 2.08 1.09 3.36-2.87-2.08-2.87 2.08 1.09-3.36-2.87-2.08h3.54z"
                        fill="#ffb100"
                    />
                    <path
                        className={css(styles.star2)}
                        d="M12 2.5l1.09 3.36h3.54l-2.87 2.08 1.09 3.36-2.87-2.08-2.87 2.08 1.09-3.36-2.87-2.08h3.54z"
                        fill="#ffb100"
                    />
                </svg>
            )}
        </View>
    );
};

const star1Keyframes = {
    from: {
        opacity: 0,
        transform: "rotate(-6deg) translate(0px,15px) scale(.8)",
    },
    "10%": {
        opacity: 1,
    },
    to: {
        opacity: 0,
        transform: "rotate(-8deg) translate(0px,-3px) scale(.8)",
    },
};

const star2Keyframes = {
    from: {
        opacity: 0,
        visibility: "hidden",
        transform: "rotate(12deg) translate(15px,10px) scale(.65)",
    },
    "35%": {
        visibility: "hidden",
    },
    "36%": {
        visibility: "visible",
        opacity: 1,
    },
    to: {
        opacity: 0,
        transform: "rotate(20deg) translate(15px,-5px) scale(.65)",
    },
};

const styles = StyleSheet.create({
    hoverFavoriteButton: {
        padding: "5px",
        color: color.gold,
        ":hover": {
            color: color.gold,
        },
    },
    starAnimation: {
        position: "absolute",
        top: "-15px",
        left: "-3px",
        pointerEvents: "none",
    },
    star1: {
        // @ts-expect-error - TS2322 - Type '{ readonly from: { readonly opacity: 0; readonly height: "50%"; }; readonly to: { readonly opacity: 1; readonly height: "115%"; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: star1Keyframes,
        animationDuration: "1500ms",
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
    },
    star2: {
        // @ts-expect-error - TS2322 - Type '{ readonly from: { readonly opacity: 0; readonly height: "50%"; }; readonly to: { readonly opacity: 1; readonly height: "115%"; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: star2Keyframes,
        animationDuration: "1200ms",
        animationTimingFunction: "ease-out",
        animationFillMode: "forwards",
    },
});

export default FavoriteButton;
